import { InstructionModal } from './InstructionModal';
import { Box, Button, Card, CardContent, CardMedia, Stack, Typography } from '@mui/material';
import { Icons, PowerProfileMode, powerProfileModes } from 'greener-ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PowerProfileGenerationInstructionsProps {
  mode: PowerProfileMode;
}

export const PowerProfileGenerationInstructions = ({ mode }: PowerProfileGenerationInstructionsProps) => {
  const { t } = useTranslation();
  const [showManualModeInstructions, setShowManualModeInstructions] = useState(false);
  const [showFileFormatInstructions, setShowFileFormatInstructions] = useState(false);

  if (mode === powerProfileModes.manual) {
    return (
      <InstructionModal
        trigger={
          <Box
            data-testid="power-profile-instructions-icon"
            component="button"
            onClick={() => setShowManualModeInstructions(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              marginLeft: 1.5,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Icons.HelpOutline sx={{ fontSize: 20 }} />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
              }}
            >
              {t('blueprints:wizard.fields.powerProfileGeneration.modeInfo.manualTriggerLabel')}
            </Typography>
          </Box>
        }
        title={t('blueprints:wizard.fields.powerProfileGeneration.modeInfo.title')}
        open={showManualModeInstructions}
        onClose={() => setShowManualModeInstructions(false)}
      >
        <Box>
          <Typography variant="body2" paragraph>
            {t('blueprints:wizard.fields.powerProfileGeneration.modeInfo.manual')
              .split('\n\n')
              .map((paragraph, index) => (
                <Box key={index} component="span" sx={{ display: 'block', mb: index < 3 ? 2 : 0 }}>
                  {paragraph}
                </Box>
              ))}
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 600 }}>
                {t('blueprints:wizard.fields.powerProfileGeneration.modeInfo.warningLabel')}
              </Box>{' '}
              {t('blueprints:wizard.fields.powerProfileGeneration.modeInfo.warningText')}
            </Typography>
          </Box>
        </Box>
      </InstructionModal>
    );
  }

  if (mode === powerProfileModes.file) {
    return (
      <InstructionModal
        trigger={
          <Box
            data-testid="power-profile-instructions-icon"
            component="button"
            onClick={() => setShowFileFormatInstructions(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              marginLeft: 1.5,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Icons.HelpOutline sx={{ fontSize: 20 }} />
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
              }}
            >
              {t('blueprints:wizard.fields.powerProfileGeneration.modeInfo.fileTriggerLabel')}
            </Typography>
          </Box>
        }
        title={t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.title')}
        open={showFileFormatInstructions}
        onClose={() => setShowFileFormatInstructions(false)}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
          {/* Instructions */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.title')}
            </Typography>

            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.format.label')}
                </Box>{' '}
                {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.format.content')}
              </Typography>

              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.size.label')}
                </Box>{' '}
                {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.size.content')}
              </Typography>

              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.columns.label')}
                </Box>
                <Box sx={{ mt: 0.5 }}>
                  <Box component="span" sx={{ textDecoration: 'underline' }}>
                    {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.columns.exactCount')}
                  </Box>{' '}
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.columns.withHeaders')}
                </Box>
                <Box sx={{ mt: 0.5 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.columns.columnNames')}
                  </Box>
                </Box>
                <Box sx={{ mt: 0.5, fontStyle: 'italic' }}>
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.columns.additionalInfo')}
                </Box>
              </Typography>

              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.timeFormat.label')}
                </Box>{' '}
                {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.timeFormat.content')}
              </Typography>

              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.powerFormat.label')}
                </Box>{' '}
                {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.powerFormat.content')}
              </Typography>

              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.unitFormat.label')}
                </Box>{' '}
                {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.unitFormat.content')}
              </Typography>

              <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.dataProcessing.label')}
                </Box>{' '}
                {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.requirements.dataProcessing.content')}
              </Typography>
            </Box>
          </Box>

          {/* File Preview */}
          <Box
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'stretch' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{ textAlign: { xs: 'center', md: 'left' } }}
            >
              {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.downloadHint')}
            </Typography>
            <Card
              sx={{
                maxWidth: 300,
                bgcolor: 'white',
                boxShadow: 2,
                alignSelf: { xs: 'center', md: 'center' },
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image="/static/images/example-power-profile-thumbnail.png"
                alt="Power Profile File Example"
                sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
              />
              <CardContent sx={{ p: 2 }}>
                <Button
                  data-testid="file-template-download-link"
                  variant="outlined"
                  size="small"
                  startIcon={<Icons.FileDownload />}
                  fullWidth
                  href="/static/files/power-profile-example.xlsx"
                  download="power-profile-example.xlsx"
                >
                  {t('blueprints:wizard.fields.powerProfileGeneration.fileFormat.downloadExample')}
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </InstructionModal>
    );
  }

  return null;
};
